import { Navigate, Route, Routes } from "react-router-dom";
import { AuthGuard } from "@/components/AuthGuard";
import { Layout } from "@/components/Layout";
import { SessionGate } from "@/components/SessionGate";
import { PasswordChangeGate } from "@/components/PasswordChangeGate";
import { useAuth } from "@/store/auth";
import { lazyPage } from "@/lazyPage";

// The sign-in screen is the first paint, so it is the one page in the main bundle.
import { LoginPage } from "@/features/auth/LoginPage";

// Everything behind sign-in loads when first opened. Layout shows a skeleton meanwhile.
const DashboardPage = lazyPage(() => import("@/features/dashboard/DashboardPage"), "DashboardPage");

const ShiftsPage = lazyPage(() => import("@/features/workforce/ShiftsPage"), "ShiftsPage");
const DutyTypesPage = lazyPage(() => import("@/features/workforce/CodeMasterPages"), "DutyTypesPage");
const StaffCategoriesPage = lazyPage(() => import("@/features/workforce/CodeMasterPages"), "StaffCategoriesPage");
const SkillsPage = lazyPage(() => import("@/features/workforce/CodeMasterPages"), "SkillsPage");
const EmploymentTypesPage = lazyPage(() => import("@/features/workforce/CodeMasterPages"), "EmploymentTypesPage");
const LeaveTypesPage = lazyPage(() => import("@/features/workforce/LeaveTypesPage"), "LeaveTypesPage");
const HolidayCalendarsPage = lazyPage(() => import("@/features/workforce/CalendarPages"), "HolidayCalendarsPage");
const WeeklyOffPatternsPage = lazyPage(() => import("@/features/workforce/CalendarPages"), "WeeklyOffPatternsPage");

const DepartmentsPage = lazyPage(() => import("@/features/master/OrganisationPages"), "DepartmentsPage");
const UnitsPage = lazyPage(() => import("@/features/master/OrganisationPages"), "UnitsPage");
const DesignationsPage = lazyPage(() => import("@/features/master/OrganisationPages"), "DesignationsPage");
const LocationsPage = lazyPage(() => import("@/features/master/OrganisationPages"), "LocationsPage");
const EmployeesPage = lazyPage(() => import("@/features/master/EmployeesPage"), "EmployeesPage");

const RosterListPage = lazyPage(() => import("@/features/roster/RosterListPage"), "RosterListPage");
const RosterDetailPage = lazyPage(() => import("@/features/roster/RosterDetailPage"), "RosterDetailPage");
const DailyDutyPage = lazyPage(() => import("@/features/roster/DailyDutyPage"), "DailyDutyPage");
const RosterRulesPage = lazyPage(() => import("@/features/roster/RosterRulesPage"), "RosterRulesPage");
const StaffingRequirementsPage = lazyPage(() => import("@/features/roster/StaffingRequirementsPage"), "StaffingRequirementsPage");

const AttendanceBoardPage = lazyPage(() => import("@/features/attendance/AttendanceBoardPage"), "AttendanceBoardPage");
const AttendanceQueuesPage = lazyPage(() => import("@/features/attendance/AttendanceQueuesPage"), "AttendanceQueuesPage");
const AttendanceDevicesPage = lazyPage(() => import("@/features/attendance/AttendanceDevicesPage"), "AttendanceDevicesPage");
const AttendancePolicyPage = lazyPage(() => import("@/features/attendance/AttendancePolicyPage"), "AttendancePolicyPage");
const MarkAttendancePage = lazyPage(() => import("@/features/attendance/MarkAttendancePage"), "MarkAttendancePage");

const TheatreBoardPage = lazyPage(() => import("@/features/theatre/TheatreBoardPage"), "TheatreBoardPage");
const TheatreSchedulePage = lazyPage(() => import("@/features/theatre/TheatreSchedulePage"), "TheatreSchedulePage");
const TheatreSetupPage = lazyPage(() => import("@/features/theatre/TheatreSetupPage"), "TheatreSetupPage");

const FeatureFlagsPage = lazyPage(() => import("@/features/admin/AdminPages"), "FeatureFlagsPage");
const ModulesPage = lazyPage(() => import("@/features/admin/AdminPages"), "ModulesPage");
const UsersPage = lazyPage(() => import("@/features/admin/UsersPage"), "UsersPage");
const RolesPage = lazyPage(() => import("@/features/admin/RolesPage"), "RolesPage");
const HealNTrackPage = lazyPage(() => import("@/features/admin/HealNTrackPage"), "HealNTrackPage");

const ApprovalsPage = lazyPage(() => import("@/features/workflow/ApprovalsPage"), "ApprovalsPage");
const SwapsPage = lazyPage(() => import("@/features/workflow/SwapsPage"), "SwapsPage");
const ShiftChangesPage = lazyPage(() => import("@/features/workflow/ShiftChangesPage"), "ShiftChangesPage");

const LeavePage = lazyPage(() => import("@/features/leave/LeavePage"), "LeavePage");
const LeaveEntitlementsPage = lazyPage(() => import("@/features/leave/LeaveEntitlementsPage"), "LeaveEntitlementsPage");
const ReportHubPage = lazyPage(() => import("@/features/reports/ReportHubPage"), "ReportHubPage");
const ReportViewerPage = lazyPage(() => import("@/features/reports/ReportViewerPage"), "ReportViewerPage");

const MyTodayPage = lazyPage(() => import("@/features/me/MyTodayPage"), "MyTodayPage");
const MyRosterPage = lazyPage(() => import("@/features/me/MyRosterPage"), "MyRosterPage");
const MyAttendancePage = lazyPage(() => import("@/features/me/MyAttendancePage"), "MyAttendancePage");

/**
 * Where "/" leads: the operational dashboard for someone who can read it, their own day for a member
 * of staff. One application, one sign-in — what opens first follows the role.
 */
function Home() {
  const has = useAuth((s) => s.has);
  const linked = useAuth((s) => Boolean(s.user?.employeeId));
  const to = has("duty.view") ? "/dashboard" : has("selfservice.view") && linked ? "/my/today" : "/dashboard";
  return <Navigate to={to} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <AuthGuard>
            {/* An administrator-issued password is replaced before anything else opens. */}
            <PasswordChangeGate>
              {/* Holds the shell back until /me lands, so the sidebar renders once, correctly. */}
              <SessionGate>
                <Layout />
              </SessionGate>
            </PasswordChangeGate>
          </AuthGuard>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />

        <Route path="/roster" element={<RosterListPage />} />
        <Route path="/roster/day" element={<DailyDutyPage />} />
        <Route path="/roster/rules" element={<RosterRulesPage />} />
        <Route path="/roster/staffing" element={<StaffingRequirementsPage />} />
        <Route path="/roster/:id" element={<RosterDetailPage />} />

        <Route path="/my/today" element={<MyTodayPage />} />
        <Route path="/my/roster" element={<MyRosterPage />} />
        <Route path="/my/attendance" element={<MyAttendancePage />} />

        <Route path="/workflow/approvals" element={<ApprovalsPage />} />
        <Route path="/workflow/swaps" element={<SwapsPage />} />
        <Route path="/workflow/shift-changes" element={<ShiftChangesPage />} />

        <Route path="/leave" element={<LeavePage />} />
        <Route path="/leave/entitlements" element={<LeaveEntitlementsPage />} />

        <Route path="/reports" element={<ReportHubPage />} />
        <Route path="/reports/:reportKey" element={<ReportViewerPage />} />

        <Route path="/attendance" element={<AttendanceBoardPage />} />
        <Route path="/attendance/mark" element={<MarkAttendancePage />} />

        <Route path="/theatre" element={<TheatreBoardPage />} />
        <Route path="/theatre/schedule" element={<TheatreSchedulePage />} />
        <Route path="/theatre/setup" element={<TheatreSetupPage />} />
        <Route path="/attendance/queues" element={<AttendanceQueuesPage />} />
        <Route path="/attendance/devices" element={<AttendanceDevicesPage />} />
        <Route path="/attendance/policy" element={<AttendancePolicyPage />} />

        <Route path="/workforce/shifts" element={<ShiftsPage />} />
        <Route path="/workforce/duty-types" element={<DutyTypesPage />} />
        <Route path="/workforce/staff-categories" element={<StaffCategoriesPage />} />
        <Route path="/workforce/skills" element={<SkillsPage />} />
        <Route path="/workforce/employment-types" element={<EmploymentTypesPage />} />
        <Route path="/workforce/leave-types" element={<LeaveTypesPage />} />
        <Route path="/workforce/holiday-calendars" element={<HolidayCalendarsPage />} />
        <Route path="/workforce/weekly-off-patterns" element={<WeeklyOffPatternsPage />} />

        <Route path="/master/departments" element={<DepartmentsPage />} />
        <Route path="/master/units" element={<UnitsPage />} />
        <Route path="/master/designations" element={<DesignationsPage />} />
        <Route path="/master/locations" element={<LocationsPage />} />
        <Route path="/master/employees" element={<EmployeesPage />} />

        <Route path="/admin/feature-flags" element={<FeatureFlagsPage />} />
        <Route path="/admin/modules" element={<ModulesPage />} />
        <Route path="/admin/users" element={<UsersPage />} />
        <Route path="/admin/roles" element={<RolesPage />} />
        <Route path="/admin/healntrack" element={<HealNTrackPage />} />

        <Route path="/" element={<Home />} />
      </Route>

      {/* Anything unrecognised goes home rather than to a dead end. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
